import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  Contract,
  ContractType,
  Displacement,
  ExtraValue,
  Invoice,
  LooseService,
  PendingBilling,
} from '@erp/shared';
import { errorMessage as sharedErrorMessage } from '@/features/tickets/ticket-format';

export type ContractStatusFilter = 'active' | 'expired' | 'canceled' | 'all';
export type InvoiceStatusFilter = 'billed' | 'paid' | 'reversed' | 'all';

export function contractTypesOptions() {
  return queryOptions({
    queryKey: ['erp', 'billing', 'contract-types'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ContractType[]>('/billing/contract-types', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface ContractsParams {
  client_ids?: number[];
  status?: ContractStatusFilter;
}

export function contractsOptions(params: ContractsParams) {
  return queryOptions({
    queryKey: ['erp', 'billing', 'contracts', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Contract[]>('/billing/contracts', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface PendingParams {
  competence: string;
  client_ids?: number[];
}

export function pendingBillingsOptions(params: PendingParams) {
  return queryOptions({
    queryKey: ['erp', 'billing', 'pending', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<PendingBilling[]>('/billing/pending', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface InvoicesParams {
  billing_start?: string;
  billing_end?: string;
  client_ids?: number[];
  status?: InvoiceStatusFilter;
  nfe?: string;
}

export function invoicesOptions(params: InvoicesParams) {
  return queryOptions({
    queryKey: ['erp', 'billing', 'invoices', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Invoice[]>('/billing/invoices', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Cadastros financeiros (v9): deslocamentos, serviços avulsos, valores ────
// extras — contratos do @erp/shared (campos achatados, resposta array puro).

export function displacementsOptions() {
  return queryOptions({
    queryKey: ['erp', 'billing', 'displacements'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Displacement[]>('/billing/displacements', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function looseServicesOptions() {
  return queryOptions({
    queryKey: ['erp', 'billing', 'loose-services'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<LooseService[]>('/billing/loose-services', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function extraValuesOptions() {
  return queryOptions({
    queryKey: ['erp', 'billing', 'extra-values'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ExtraValue[]>('/billing/extra-values', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Códigos de erro ESPECÍFICOS do billing com mensagem amigável em pt-BR —
 * códigos transversais (validation_error, permission_denied…) moram no
 * SHARED_ERROR_LABELS de ticket-format, resolvidos aqui via fallback.
 */
const ERROR_LABELS: Record<string, string> = {
  // cadastros financeiros / lançamentos (v9)
  catalog_not_for_client: 'Este cadastro não vale para o cliente do ticket.',
  contract_not_of_client: 'O contrato selecionado não pertence ao cliente escolhido.',
  contract_without_client: 'Escolha um cliente antes de vincular um contrato.',
  contract_not_found: 'Contrato não encontrado — a lista pode estar desatualizada.',
  client_not_found: 'Cliente não encontrado — a lista pode estar desatualizada.',
  source_not_found: 'Cadastro não encontrado — a lista pode estar desatualizada.',
  service_kind_required: 'Escolha o tipo de hora (interno, remoto ou externo).',
  service_kind_not_applicable: 'Tipo de hora só se aplica a serviços avulsos.',
  invalid_quantity: 'Quantidade inválida.',
  invalid_unit_value: 'Valor unitário inválido.',
  unit_value_required: 'Informe o valor unitário.',
  name_required: 'Informe o nome do lançamento.',
  displacement_not_found: 'Deslocamento não encontrado.',
  loose_service_not_found: 'Serviço avulso não encontrado.',
  extra_value_not_found: 'Valor extra não encontrado.',
  charge_not_found: 'Lançamento não encontrado.',
  charges_unavailable: 'Lançamentos indisponíveis no momento — tente de novo.',
};

export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const code = (err as { error: string }).error;
    // fallback nos transversais do app (SHARED_ERROR_LABELS) e, por fim, no código cru
    return ERROR_LABELS[code] ?? sharedErrorMessage(err);
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}
